// ============================================================
// INGREDIABLE — API Service Layer (Supabase)
// ============================================================

import { supabase } from "./supabase-client";

// ────────────────────────────────────────────────────────────
// INPUT SANITIZATION
// Strips HTML/script tags and trims whitespace from any
// user-supplied string before it reaches the database.
// This is a defence-in-depth measure — Supabase uses
// parameterised queries so SQL injection is already
// handled, but this blocks stored-XSS attempts.
// ────────────────────────────────────────────────────────────

/** Remove HTML tags and trim whitespace from a user string. */
export function sanitizeStr(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, "")          // strip HTML tags
    .replace(/javascript:/gi, "")     // kill js: URI scheme
    .replace(/on\w+\s*=/gi, "")      // strip onerror= / onclick= etc.
    .trim()
    .slice(0, 2000);                   // hard length cap
}

/** Sanitize every string field in a plain object (shallow). */
export function sanitizeRecord(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = typeof v === "string" ? sanitizeStr(v) : v;
  }
  return out;
}

function guard() {
  if (!supabase) throw new Error("Supabase not configured. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env");
}

// ────────────────────────────────────────────────────────────
// AUTH
// ────────────────────────────────────────────────────────────

export const auth = {
  async signUp(email: string, password: string, displayName: string) {
    guard();
    const { data, error } = await supabase!.auth.signUp({
      email,
      password,
      options: { data: { display_name: sanitizeStr(displayName) } },
    });
    if (error) throw error;
    return data;
  },

  async signIn(email: string, password: string) {
    guard();
    const { data, error } = await supabase!.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  },

  async signOut() {
    if (!supabase) return;
    // scope: 'local' clears the local session and fires SIGNED_OUT without
    // making a server request, so it never fails due to network or token issues.
    await supabase.auth.signOut({ scope: 'local' });
  },

  async getSession(): Promise<unknown> {
    if (!supabase) return null;
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  async getUser() {
    guard();
    const { data, error } = await supabase!.auth.getUser();
    if (error) throw error;
    return data.user;
  },

  async resetPassword(email: string, redirectTo?: string) {
    guard();
    const url = redirectTo ?? (typeof window !== "undefined" ? window.location.origin : undefined);
    const { error } = await supabase!.auth.resetPasswordForEmail(email, {
      redirectTo: url || undefined,
    });
    if (error) throw error;
  },

  /** Resend signup confirmation email */
  async resendConfirmationEmail(email: string) {
    guard();
    const { error } = await supabase!.auth.resend({
      type: "signup",
      email,
    });
    if (error) throw error;
  },

  onAuthStateChange(callback: (event: string, session: unknown) => void) {
    if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };
    return supabase.auth.onAuthStateChange(callback);
  },
};

// ────────────────────────────────────────────────────────────
// PROFILES
// ────────────────────────────────────────────────────────────

export const profiles = {
  async get(userId: string) {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();
    if (error) throw error;
    return data;
  },

  async update(userId: string, updates: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("profiles")
      .update(sanitizeRecord(updates))
      .eq("id", userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// ────────────────────────────────────────────────────────────
// HOUSEHOLDS
// ────────────────────────────────────────────────────────────

export const households = {
  /** List households for the given user (avoids extra auth.getUser() call that can hang). */
  async listForUser(userId: string) {
    if (!supabase) throw new Error("Supabase not configured");
    const { data, error } = await supabase
      .from("household_members")
      .select(
        `
        role,
        joined_at,
        household:households (*)
      `
      )
      .eq("user_id", userId);
    if (error) throw error;
    return data ?? [];
  },

  async list() {
    const user = await auth.getUser();
    if (!user) throw new Error("Not authenticated");
    return households.listForUser(user.id);
  },

  async get(householdId: string) {
    const { data, error } = await supabase
      .from("households")
      .select(
        `
        *,
        members:household_members (
          role,
          joined_at,
          profile:profiles (id, display_name, email, avatar_url)
        )
      `
      )
      .eq("id", householdId)
      .single();
    if (error) throw error;
    return data;
  },

  async create(name: string) {
    const { data, error } = await supabase.rpc("create_household_with_owner", {
      household_name: sanitizeStr(name),
    });
    if (error) throw error;
    return data as unknown as Record<string, unknown>;
  },

  async joinByCode(inviteCode: string) {
    const { data, error } = await supabase.rpc("join_household_by_code", {
      invite_code_input: inviteCode,
    });
    if (error) throw error;
    return { id: data as string };
  },

  async update(householdId: string, updates: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("households")
      .update(sanitizeRecord(updates))
      .eq("id", householdId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async delete(householdId: string) {
    const { error } = await supabase.rpc("delete_household", {
      household_id_input: householdId,
    });
    if (error) throw error;
  },

  async removeMember(householdId: string, userId: string) {
    const { error } = await supabase
      .from("household_members")
      .delete()
      .eq("household_id", householdId)
      .eq("user_id", userId);
    if (error) throw error;
  },

  async updateMemberRole(
    householdId: string,
    userId: string,
    role: string
  ) {
    const { error } = await supabase
      .from("household_members")
      .update({ role })
      .eq("household_id", householdId)
      .eq("user_id", userId);
    if (error) throw error;
  },

  async getStats(householdId: string) {
    const { data, error } = await supabase
      .from("household_stats")
      .select("*")
      .eq("household_id", householdId)
      .single();
    if (error) throw error;
    return data;
  },
};

// ────────────────────────────────────────────────────────────
// INVENTORY (map UI shape: expiry -> expiry_date, opened -> is_opened)
// ────────────────────────────────────────────────────────────

export const inventory = {
  async list(
    householdId: string,
    filters: {
      search?: string;
      category?: string;
      location?: string;
      sortBy?: string;
    } = {}
  ) {
    const { search, category, location, sortBy = "name" } = filters;
    let query = supabase
      .from("inventory_items")
      .select(
        `
        *,
        added_by_profile:profiles!inventory_items_added_by_fkey (display_name)
      `
      )
      .eq("household_id", householdId);
    if (search)
      query = query.ilike("name", `%${search}%`);
    if (category && category !== "All")
      query = query.eq("category", category);
    if (location && location !== "All")
      query = query.eq("location", location);
    switch (sortBy) {
      case "expiry":
        query = query.order("expiry_date", {
          ascending: true,
          nullsFirst: false,
        });
        break;
      case "quantity":
        query = query.order("quantity", { ascending: true });
        break;
      case "added":
        query = query.order("created_at", { ascending: false });
        break;
      default:
        query = query.order("name");
    }
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  async add(
    householdId: string,
    item: Record<string, unknown> & {
      name: string;
      category?: string;
      location?: string;
      quantity?: number;
      unit?: string;
      expiry?: string;
      opened?: boolean;
    }
  ) {
    const user = await auth.getUser();
    if (!user) throw new Error("Not authenticated");
    const { expiry, opened, ...rest } = item;
    const cleanRest = sanitizeRecord(rest as Record<string, unknown>);
    const { data, error } = await supabase
      .from("inventory_items")
      .insert({
        household_id: householdId,
        added_by: user.id,
        ...cleanRest,
        ...(expiry != null && { expiry_date: expiry }),
        ...(opened != null && { is_opened: opened }),
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(
    itemId: string,
    updates: Record<string, unknown> & {
      expiry?: string;
      opened?: boolean;
    }
  ) {
    const { expiry, opened, ...rest } = updates;
    const payload: Record<string, unknown> = { ...sanitizeRecord(rest as Record<string, unknown>) };
    if (expiry !== undefined) payload.expiry_date = expiry;
    if (opened !== undefined) payload.is_opened = opened;
    const { data, error } = await supabase
      .from("inventory_items")
      .update(payload)
      .eq("id", itemId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async remove(
    itemId: string,
    opts: {
      logWaste?: boolean;
      reason?: string;
      estimatedCost?: number;
    } = {}
  ) {
    const { logWaste = false, reason = "removed", estimatedCost } = opts;
    if (logWaste) {
      const { data: item } = await supabase
        .from("inventory_items")
        .select("*")
        .eq("id", itemId)
        .single();
      if (item) {
        const user = await auth.getUser();
        if (user) {
          await supabase.from("waste_log").insert({
            household_id: item.household_id,
            item_name: item.name,
            category: item.category,
            reason,
            quantity: item.quantity,
            unit: item.unit,
            estimated_cost: estimatedCost ?? null,
            logged_by: user.id,
          });
        }
      }
    }
    const { error } = await supabase
      .from("inventory_items")
      .delete()
      .eq("id", itemId);
    if (error) throw error;
  },

  async getExpiring(householdId: string) {
    const { data, error } = await supabase
      .from("expiring_items")
      .select("*")
      .eq("household_id", householdId);
    if (error) throw error;
    return data ?? [];
  },

  subscribeToChanges(
    householdId: string,
    callback: () => void
  ) {
    return supabase
      .channel(`inventory:${householdId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "inventory_items",
          filter: `household_id=eq.${householdId}`,
        },
        callback
      )
      .subscribe();
  },
};

// ────────────────────────────────────────────────────────────
// BARCODE / PRODUCT LOOKUP (Open Food Facts)
// ────────────────────────────────────────────────────────────

const OPEN_FOOD_FACTS_API = "https://world.openfoodfacts.org/api/v2/product";

export type ProductLookupResult = {
  name: string;
  brand?: string;
  barcode: string;
  category: string;
  confidence: number;
  imageUrl?: string;
  servingSize?: string;
  nutrition?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    fiber?: number;
    sugar?: number;
  };
  raw?: Record<string, unknown>;
};

function pickName(product: Record<string, unknown>): { name: string; brand?: string } {
  const productName = [
    product.product_name,
    product.product_name_en,
    product.product_name_fr,
    product.product_name_de,
    product.generic_name,
    product.abbreviated_product_name,
  ].find((v): v is string => typeof v === "string" && v.trim().length > 0)?.trim() || "";

  const brand = typeof product.brands === "string"
    ? product.brands.split(",")[0].trim()
    : undefined;

  // Combine brand + product name if brand isn't already in the product name
  let name = productName;
  if (brand && productName && !productName.toLowerCase().includes(brand.toLowerCase())) {
    name = `${brand} ${productName}`;
  } else if (!productName && brand) {
    name = brand;
  }

  return { name, brand: brand || undefined };
}

function extractNutrition(product: Record<string, unknown>) {
  const n = product.nutriments as Record<string, unknown> | undefined;
  if (!n) return undefined;
  const get = (key: string) => {
    const v = n[key] ?? n[`${key}_100g`] ?? n[`${key}_serving`];
    return typeof v === "number" ? Math.round(v * 10) / 10 : undefined;
  };
  const nutrition = {
    calories: get("energy-kcal"),
    protein: get("proteins"),
    carbs: get("carbohydrates"),
    fat: get("fat"),
    fiber: get("fiber"),
    sugar: get("sugars"),
  };
  // Only return if at least one value exists
  return Object.values(nutrition).some(v => v !== undefined) ? nutrition : undefined;
}

function inferCategoryFromName(name: string): string {
  const n = name.toLowerCase();
  if (/milk|cheese|yogurt|cream|butter|egg|dairy/.test(n)) return "Dairy";
  if (/chicken|beef|pork|turkey|salmon|tuna|fish|shrimp|lamb|sausage|bacon|meat/.test(n)) return "Meat";
  if (/carrot|apple|banana|spinach|lettuce|tomato|onion|pepper|broccoli|fruit|vegetable|berry|lemon|lime|orange|grape|avocado|cucumber|celery|herb|garlic|ginger/.test(n)) return "Produce";
  if (/bread|bagel|muffin|croissant|bun|roll|loaf|sourdough|tortilla|pita/.test(n)) return "Bakery";
  if (/frozen|ice cream/.test(n)) return "Frozen";
  if (/canned|can of|tinned/.test(n)) return "Canned";
  if (/juice|soda|water|coffee|tea|smoothie|lemonade/.test(n)) return "Beverages";
  if (/chip|crisp|popcorn|pretzel|cracker|cookie|candy|chocolate|granola bar|rice cake|snack/.test(n)) return "Snacks";
  if (/oil|vinegar|ketchup|mustard|mayo|sauce|dressing|salsa|hummus/.test(n)) return "Condiments";
  if (/rice|pasta|noodle|oat|flour|cereal|quinoa|grain|barley|couscous/.test(n)) return "Grains";
  if (/spice|seasoning|cumin|oregano|paprika|cinnamon|turmeric/.test(n)) return "Spices";
  return "Other";
}

function normalizeCategory(category: string, name?: string): string {
  const c = (category || "").trim();
  const lower = c.toLowerCase().replace(/^en:/, "").replace(/-/g, " ");

  if (/plant.based|vegan|vegetarian|tofu|soy/.test(lower)) return "Plant-based";
  if (/dairy|dairies|milk|cheese|yogurt|cream|butter|egg/.test(lower)) return "Dairy";
  if (/meat|beef|chicken|pork|poultry|fish|seafood|lamb|turkey|sausage/.test(lower)) return "Meat";
  if (/fruit|vegetable|produce|fresh herb/.test(lower)) return "Produce";
  if (/bread|bakery|pastry|cake|biscuit|roll|bagel|muffin/.test(lower)) return "Bakery";
  if (/frozen/.test(lower)) return "Frozen";
  if (/canned|tinned|preserved/.test(lower)) return "Canned";
  if (/^beverage|^drink|juice|soda|coffee|tea|alcohol|wine|beer/.test(lower)) return "Beverages";
  if (/snack|chip|crisp|popcorn|pretzel|cracker|nut|candy|chocolate|sweet/.test(lower)) return "Snacks";
  if (/condiment|sauce|dressing|oil|vinegar|ketchup|mustard|mayo/.test(lower)) return "Condiments";
  if (/grain|rice|pasta|cereal|flour|oat|quinoa|noodle/.test(lower)) return "Grains";
  if (/spice|seasoning|pepper|cinnamon|cumin|oregano/.test(lower)) return "Spices";

  const valid = ["Produce","Dairy","Meat","Bakery","Frozen","Canned","Beverages","Snacks","Condiments","Grains","Spices","Plant-based"];
  if (valid.includes(c)) return c;

  if (name) return inferCategoryFromName(name);
  return "Other";
}

export async function lookupProductByBarcode(barcode: string): Promise<ProductLookupResult | null> {
  const code = barcode.replace(/\D/g, "").trim();
  if (!code || code.length < 8) return null;

  async function fetchProduct(barcodeDigits: string): Promise<ProductLookupResult | null> {
    const res = await fetch(`${OPEN_FOOD_FACTS_API}/${barcodeDigits}.json`, {
      headers: { "User-Agent": "Ingrediable/1.0 (Kitchen inventory)" },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { product?: Record<string, unknown>; status?: number };
    const product = json?.product;
    if (!product || json.status !== 1) return null;
    const { name: productName, brand } = pickName(product);
    const name = productName || `Product ${barcodeDigits}`;
    const categories = (product.categories as string) || "";
    const firstCategory = normalizeCategory(
      categories.split(",").map((c) => c.trim()).filter(Boolean)[0] || "",
      name
    );
    const imageUrl = (product.image_front_small_url || product.image_small_url || product.image_url) as string | undefined;
    const servingSize = typeof product.serving_size === "string" ? product.serving_size : undefined;
    const nutrition = extractNutrition(product);
    return {
      name: name.trim(),
      brand,
      barcode: barcodeDigits,
      category: firstCategory,
      confidence: 95,
      imageUrl,
      servingSize,
      nutrition,
      raw: product,
    };
  }

  try {
    let result = await fetchProduct(code);
    if (!result && code.length === 12) result = await fetchProduct("0" + code);
    return result;
  } catch {
    return null;
  }
}

// ────────────────────────────────────────────────────────────
// SCAN HISTORY (Supabase)
// ────────────────────────────────────────────────────────────

export const scanHistory = {
  async add(params: {
    userId: string;
    householdId: string;
    barcode: string | null;
    productName: string | null;
    confidence: number | null;
    scanType: "barcode" | "photo" | "manual";
    rawResult?: Record<string, unknown> | null;
  }) {
    if (!supabase) return;
    const { error } = await supabase.from("scan_history").insert({
      user_id: params.userId,
      household_id: params.householdId,
      barcode: params.barcode,
      product_name: params.productName ? sanitizeStr(params.productName) : null,
      confidence: params.confidence,
      scan_type: params.scanType,
      raw_result: params.rawResult ?? null,
    });
    if (error) throw error;
  },

  async list(userId: string, limit = 20) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from("scan_history")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data ?? [];
  },
};

// ────────────────────────────────────────────────────────────
// SHOPPING (map UI: estCost -> estimated_cost, checked -> is_checked)
// ────────────────────────────────────────────────────────────

export const shopping = {
  async list(householdId: string) {
    const { data, error } = await supabase
      .from("shopping_items")
      .select(
        `
        *,
        added_by_profile:profiles!added_by (display_name)
      `
      )
      .eq("household_id", householdId)
      .order("is_checked")
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async add(
    householdId: string,
    item: Record<string, unknown> & {
      name: string;
      quantity?: number;
      unit?: string;
      priority?: string;
      estCost?: string | number;
      checked?: boolean;
    }
  ) {
    const user = await auth.getUser();
    if (!user) throw new Error("Not authenticated");
    const { estCost, checked, ...rest } = item;
    const cleanRest = sanitizeRecord(rest as Record<string, unknown>);
    const { data, error } = await supabase
      .from("shopping_items")
      .insert({
        household_id: householdId,
        added_by: user.id,
        ...cleanRest,
        ...(estCost != null && {
          estimated_cost:
            typeof estCost === "string" ? parseFloat(estCost) : estCost,
        }),
        ...(checked != null && { is_checked: checked }),
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async toggleCheck(itemId: string) {
    const { data: current } = await supabase
      .from("shopping_items")
      .select("is_checked")
      .eq("id", itemId)
      .single();
    const user = await auth.getUser();
    const { data, error } = await supabase
      .from("shopping_items")
      .update({
        is_checked: !current?.is_checked,
        checked_by: !current?.is_checked ? user?.id : null,
      })
      .eq("id", itemId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async remove(itemId: string) {
    const { error } = await supabase
      .from("shopping_items")
      .delete()
      .eq("id", itemId);
    if (error) throw error;
  },

  async clearChecked(householdId: string) {
    const { error } = await supabase
      .from("shopping_items")
      .delete()
      .eq("household_id", householdId)
      .eq("is_checked", true);
    if (error) throw error;
  },

  async autoGenerate(householdId: string) {
    const today = new Date().toISOString().slice(0, 10);
    const { data: items } = await supabase
      .from("inventory_items")
      .select("name, unit, quantity, expiry_date")
      .eq("household_id", householdId)
      .or(`quantity.lte.1,expiry_date.lt.${today}`);
    if (!items?.length) return [];
    const user = await auth.getUser();
    if (!user) return [];
    const newItems = items.map((i: { name: string; unit: string; quantity: number }) => ({
      household_id: householdId,
      name: i.name,
      quantity: 1,
      unit: i.unit,
      priority: i.quantity <= 1 ? "high" : "medium",
      added_by: user.id,
    }));
    const { data, error } = await supabase
      .from("shopping_items")
      .insert(newItems)
      .select();
    if (error) throw error;
    return data ?? [];
  },

  subscribeToChanges(householdId: string, callback: () => void) {
    return supabase
      .channel(`shopping:${householdId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "shopping_items",
          filter: `household_id=eq.${householdId}`,
        },
        callback
      )
      .subscribe();
  },
};

// ────────────────────────────────────────────────────────────
// WASTE
// ────────────────────────────────────────────────────────────

export const waste = {
  async list(
    householdId: string,
    opts: { period?: "week" | "month" | "quarter" | "year" } = {}
  ) {
    const periodDays: Record<string, number> = {
      week: 7,
      month: 30,
      quarter: 90,
      year: 365,
    };
    const since = new Date();
    since.setDate(since.getDate() - (periodDays[opts.period ?? "month"] ?? 30));
    const { data, error } = await supabase
      .from("waste_log")
      .select("*")
      .eq("household_id", householdId)
      .gte("wasted_at", since.toISOString().slice(0, 10))
      .order("wasted_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async log(
    householdId: string,
    entry: Record<string, unknown> & {
      item_name?: string;
      name?: string;
      category?: string;
      reason?: string;
      quantity?: number;
      unit?: string;
      estimated_cost?: number;
      wasted_at?: string;
      date?: string;
    }
  ) {
    const user = await auth.getUser();
    if (!user) throw new Error("Not authenticated");
    const { name, date, ...rest } = entry;
    const { data, error } = await supabase
      .from("waste_log")
      .insert({
        household_id: householdId,
        logged_by: user.id,
        ...rest,
        ...(name != null && { item_name: name }),
        ...(date != null && { wasted_at: date }),
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async getSummary(householdId: string) {
    const { data, error } = await supabase
      .from("waste_summary")
      .select("*")
      .eq("household_id", householdId);
    if (error) throw error;
    return data;
  },
};

// ────────────────────────────────────────────────────────────
// ALERTS
// ────────────────────────────────────────────────────────────

export const alerts = {
  async list(householdId: string, opts?: { resolved?: boolean }) {
    let query = supabase
      .from("alerts")
      .select("*")
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });
    if (opts?.resolved !== undefined)
      query = query.eq("is_resolved", opts.resolved);
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  async resolve(alertId: string) {
    const user = await auth.getUser();
    const { error } = await supabase
      .from("alerts")
      .update({
        is_resolved: true,
        resolved_by: user?.id ?? null,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", alertId);
    if (error) throw error;
  },

  async remove(alertId: string) {
    const { error } = await supabase.from("alerts").delete().eq("id", alertId);
    if (error) throw error;
  },

  async getUnreadCount(householdId: string) {
    const { count, error } = await supabase
      .from("alerts")
      .select("*", { count: "exact", head: true })
      .eq("household_id", householdId)
      .eq("is_resolved", false);
    if (error) throw error;
    return count ?? 0;
  },

  async create(householdId: string, alert: { item_id?: string; type: string; message: string; severity: string }) {
    const { error } = await supabase.from("alerts").insert({
      household_id: householdId,
      item_id: alert.item_id ?? null,
      type: alert.type,
      message: alert.message,
      severity: alert.severity,
      is_resolved: false,
    });
    if (error) throw error;
  },

  /** Generate expiry + low-stock alerts for all items, skipping ones that already have an open alert. */
  async generateForHousehold(householdId: string, items: Array<{ id: string; name: string; expiry: string; quantity: number }>) {
    if (!items.length) return;
    const today = new Date();
    // Fetch existing unresolved alert item_ids so we don't double-create
    const { data: existing } = await supabase
      .from("alerts")
      .select("item_id, type")
      .eq("household_id", householdId)
      .eq("is_resolved", false);
    const existingSet = new Set((existing ?? []).map((a: Record<string, unknown>) => `${a.item_id}:${a.type}`));

    const toCreate: Array<{ item_id: string; type: string; message: string; severity: string }> = [];
    for (const item of items) {
      const days = Math.ceil((new Date(item.expiry).getTime() - today.getTime()) / 86400000);
      if (days < 0 && !existingSet.has(`${item.id}:expired`)) {
        toCreate.push({ item_id: item.id, type: "expired", message: `${item.name} has expired`, severity: "critical" });
      } else if (days >= 0 && days <= 3 && !existingSet.has(`${item.id}:expiry_warning`)) {
        toCreate.push({ item_id: item.id, type: "expiry_warning", message: `${item.name} expires in ${days} day${days !== 1 ? "s" : ""}`, severity: days === 0 ? "critical" : days <= 1 ? "high" : "medium" });
      }
      if (item.quantity <= 1 && !existingSet.has(`${item.id}:low_stock`)) {
        toCreate.push({ item_id: item.id, type: "low_stock", message: `${item.name} is running low (${item.quantity} left)`, severity: "medium" });
      }
    }
    if (!toCreate.length) return;
    await supabase.from("alerts").insert(
      toCreate.map(a => ({ household_id: householdId, ...a, is_resolved: false }))
    );
  },

  subscribeToChanges(householdId: string, callback: () => void) {
    return supabase
      .channel(`alerts:${householdId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "alerts",
          filter: `household_id=eq.${householdId}`,
        },
        callback
      )
      .subscribe();
  },
};

// ────────────────────────────────────────────────────────────
// RECIPES
// ────────────────────────────────────────────────────────────

export const recipes = {
  async list(filters?: { search?: string; cuisine?: string }) {
    let query = supabase
      .from("recipes")
      .select("*")
      .order("rating", { ascending: false });
    if (filters?.search)
      query = query.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      );
    if (filters?.cuisine && filters.cuisine !== "All")
      query = query.eq("cuisine", filters.cuisine);
    const { data, error } = await query;
    if (error) throw error;
    return data ?? [];
  },

  async get(recipeId: string) {
    const { data, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("id", recipeId)
      .single();
    if (error) throw error;
    return data;
  },
};

// ────────────────────────────────────────────────────────────
// USER RECIPES
// ────────────────────────────────────────────────────────────
export const userRecipesApi = {
  async list(householdId: string) {
    const { data, error } = await supabase
      .from("user_recipes")
      .select("*")
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r: Record<string, unknown>) => ({
      id: r.id,
      name: r.name,
      cuisine: r.cuisine ?? "Other",
      difficulty: r.difficulty ?? "Easy",
      prep: r.prep_minutes ?? 0,
      cook: r.cook_minutes ?? 0,
      servings: r.servings ?? 2,
      desc: r.description ?? "",
      ingredients: r.ingredients ?? [],
      steps: r.steps ?? [],
      rating: r.rating ?? 4.5,
      isTemplate: false,
    }));
  },

  async add(householdId: string, recipe: Record<string, unknown>) {
    const { data, error } = await supabase
      .from("user_recipes")
      .insert({
        household_id: householdId,
        name: recipe.name,
        cuisine: recipe.cuisine ?? "Other",
        difficulty: recipe.difficulty ?? "Easy",
        prep_minutes: recipe.prep ?? 0,
        cook_minutes: recipe.cook ?? 0,
        servings: recipe.servings ?? 2,
        description: recipe.desc ?? "",
        ingredients: recipe.ingredients ?? [],
        steps: recipe.steps ?? [],
        rating: recipe.rating ?? 4.5,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async remove(recipeId: string) {
    const { error } = await supabase.from("user_recipes").delete().eq("id", recipeId);
    if (error) throw error;
  },
};

// ────────────────────────────────────────────────────────────
// MEAL PLAN (single JSONB blob per household)
// ────────────────────────────────────────────────────────────
export const mealPlanApi = {
  async get(householdId: string): Promise<Record<string, unknown>> {
    const { data, error } = await supabase
      .from("meal_plans")
      .select("plan_data")
      .eq("household_id", householdId)
      .maybeSingle();
    if (error) throw error;
    return (data as { plan_data?: Record<string, unknown> } | null)?.plan_data ?? {};
  },

  async save(householdId: string, plan: Record<string, unknown>) {
    const { error } = await supabase
      .from("meal_plans")
      .upsert({ household_id: householdId, plan_data: plan, updated_at: new Date().toISOString() }, { onConflict: "household_id" });
    if (error) throw error;
  },
};

export function unsubscribe(channel: { unsubscribe?: () => void } | null) {
  if (channel?.unsubscribe) channel.unsubscribe();
}

// ────────────────────────────────────────────────────────────
// CIRCLES
// ────────────────────────────────────────────────────────────

export type CircleRow = {
  id: string;
  name: string;
  description?: string;
  created_by?: string;
  invite_code: string;
  created_at: string;
  members?: CircleMemberRow[];
};

export type CircleMemberRow = {
  id: string;
  circle_id: string;
  household_id: string;
  household_name?: string;
  role: "admin" | "member";
  sharing_enabled: boolean;
  shared_categories?: string[] | null;
  joined_at: string;
};

export type ItemRequestRow = {
  id: string;
  circle_id: string;
  requesting_household_id: string;
  requesting_household_name?: string;
  lending_household_id: string;
  lending_household_name?: string;
  item_name: string;
  item_id?: string | null;
  quantity: number;
  unit?: string | null;
  note?: string | null;
  status: "pending" | "accepted" | "rejected" | "completed" | "cancelled" | "expired";
  created_by?: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  messages?: RequestMessageRow[];
};

export type RequestMessageRow = {
  id: string;
  request_id: string;
  household_id: string;
  sender_id?: string;
  sender_name?: string;
  message: string;
  created_at: string;
};

export type CircleMessageRow = {
  id: string;
  circle_id: string;
  household_id: string;
  sender_id?: string;
  sender_name?: string;
  message: string;
  created_at: string;
};

export const circlesApi = {
  /** List all circles the current user's households belong to */
  async list(householdId: string): Promise<CircleRow[]> {
    guard();
    // Uses SECURITY DEFINER RPC (006e) — bypasses all RLS policy chains.
    // Direct table queries trigger Postgres recursion when circles_select
    // calls is_circle_member() which re-enters circle_members policies.
    const { data, error } = await supabase!.rpc("list_user_circles", {
      p_household_id: householdId,
    });
    if (error) throw error;
    return ((data as unknown[]) || []).map((r: unknown) => {
      const row = r as { id: string; name: string; description: string; invite_code: string; created_by: string; created_at: string; my_role: string; sharing_enabled: boolean; shared_categories: string[]|null };
      return {
        id: row.id,
        name: row.name,
        description: row.description,
        invite_code: row.invite_code,
        created_by: row.created_by,
        created_at: row.created_at,
        _myRole: row.my_role,
        _sharingEnabled: row.sharing_enabled,
        _sharedCategories: row.shared_categories,
      } as CircleRow & { _myRole: string; _sharingEnabled: boolean; _sharedCategories: string[]|null };
    });
  },

  /** Create a new circle; creator's household automatically becomes admin member */
  async create(name: string, description: string, householdId: string): Promise<CircleRow> {
    guard();
    // Try the atomic RPC first (requires 006d migration). Wrap in a 15s timeout so the
    // UI never hangs forever if Supabase is cold-starting or the function doesn't exist yet.
    const rpcCall = supabase!.rpc("create_circle", {
      p_name: name,
      p_description: description,
      p_household_id: householdId,
    });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Supabase timed out — project may be waking up. Please try again in a few seconds.")), 15000)
    );
    let data: unknown, error: unknown;
    try {
      const result = await Promise.race([rpcCall, timeoutPromise]) as { data: unknown; error: unknown };
      data = result.data;
      error = result.error;
    } catch (e) {
      throw e;
    }
    if (error) {
      const err = error as { message?: string; code?: string };
      // If the RPC doesn't exist yet (006d not run), fall back to two-step insert
      if (err.code === "PGRST202" || (err.message ?? "").includes("create_circle")) {
        console.warn("[circlesApi.create] create_circle RPC not found, falling back to two-step insert");
        const { data: newCircle, error: cErr } = await supabase!
          .from("circles")
          .insert({ name, description })
          .select()
          .single();
        if (cErr) throw cErr;
        const c = newCircle as CircleRow;
        const { error: mErr } = await supabase!
          .from("circle_members")
          .insert({ circle_id: c.id, household_id: householdId, role: "admin", sharing_enabled: false });
        if (mErr) throw mErr;
        return c;
      }
      throw error;
    }
    const row = Array.isArray(data) ? (data as CircleRow[])[0] : data as CircleRow;
    return row;
  },

  /** Join an existing circle by invite code */
  async joinByCode(inviteCode: string, householdId: string): Promise<CircleRow> {
    guard();
    const { data: circle, error: cErr } = await supabase!
      .from("circles")
      .select()
      .eq("invite_code", inviteCode.trim().toLowerCase())
      .single();
    if (cErr || !circle) throw new Error("Circle not found. Check the invite code and try again.");
    // Check not already a member
    const { data: existing } = await supabase!
      .from("circle_members")
      .select("id")
      .eq("circle_id", circle.id)
      .eq("household_id", householdId)
      .maybeSingle();
    if (existing) throw new Error("Your household is already in this circle.");
    const { error: mErr } = await supabase!
      .from("circle_members")
      .insert({ circle_id: circle.id, household_id: householdId, role: "member", sharing_enabled: false });
    if (mErr) throw mErr;
    return circle as CircleRow;
  },

  /** Update sharing settings for this household in a circle */
  async updateSharing(circleId: string, householdId: string, sharingEnabled: boolean, sharedCategories?: string[] | null) {
    guard();
    const { error } = await supabase!
      .from("circle_members")
      .update({ sharing_enabled: sharingEnabled, shared_categories: sharedCategories ?? null })
      .eq("circle_id", circleId)
      .eq("household_id", householdId);
    if (error) throw error;
  },

  /** List members of a circle */
  async listMembers(circleId: string): Promise<CircleMemberRow[]> {
    guard();
    // Uses SECURITY DEFINER RPC to avoid RLS recursion on circle_members
    const { data, error } = await supabase!.rpc("list_circle_members", { p_circle_id: circleId });
    if (error) throw error;
    return (data as CircleMemberRow[]) ?? [];
  },

  /** Leave a circle */
  async leave(circleId: string, householdId: string) {
    guard();
    const { error } = await supabase!
      .from("circle_members")
      .delete()
      .eq("circle_id", circleId)
      .eq("household_id", householdId);
    if (error) throw error;
  },

  /** Get shared inventory items visible to circle members from other households */
  async getSharedInventory(circleId: string, viewingHouseholdId: string) {
    guard();
    // Uses SECURITY DEFINER RPC to read across household boundaries safely
    const { data, error } = await supabase!.rpc("get_circle_shared_inventory", {
      p_circle_id: circleId,
      p_viewing_household: viewingHouseholdId,
    });
    if (error) throw error;
    if (!data || (data as unknown[]).length === 0) return [];

    // Group flat rows by household
    const map = new Map<string, { householdId: string; householdName: string; items: unknown[] }>();
    for (const row of data as Array<{ household_id: string; household_name: string; item_id: string; item_name: string; category: string; quantity: number; unit: string; expiry: string }>) {
      if (!map.has(row.household_id)) {
        map.set(row.household_id, { householdId: row.household_id, householdName: row.household_name, items: [] });
      }
      map.get(row.household_id)!.items.push({ id: row.item_id, name: row.item_name, category: row.category, quantity: row.quantity, unit: row.unit, expiry: row.expiry });
    }
    return Array.from(map.values());
  },
};

// ────────────────────────────────────────────────────────────
// ITEM REQUESTS
// ────────────────────────────────────────────────────────────

export const itemRequestsApi = {
  /** List all requests involving this household (sent or received) */
  async list(householdId: string): Promise<ItemRequestRow[]> {
    guard();
    const { data, error } = await supabase!
      .from("item_requests")
      .select("*")
      .or(`requesting_household_id.eq.${householdId},lending_household_id.eq.${householdId}`)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data as ItemRequestRow[]) ?? [];
  },

  /** Create a new request */
  async create(req: {
    circleId: string;
    requestingHouseholdId: string;
    lendingHouseholdId: string;
    itemName: string;
    itemId?: string;
    quantity: number;
    unit?: string;
    note?: string;
  }): Promise<ItemRequestRow> {
    guard();
    const { data, error } = await supabase!
      .from("item_requests")
      .insert({
        circle_id: req.circleId,
        requesting_household_id: req.requestingHouseholdId,
        lending_household_id: req.lendingHouseholdId,
        item_name: req.itemName,
        item_id: req.itemId ?? null,
        quantity: req.quantity,
        unit: req.unit ?? null,
        note: req.note ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as ItemRequestRow;
  },

  /** Update the status of a request (accept, reject, complete, cancel) */
  async updateStatus(requestId: string, status: ItemRequestRow["status"]) {
    guard();
    const { error } = await supabase!
      .from("item_requests")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", requestId);
    if (error) throw error;
  },

  /** Get messages for a request */
  async getMessages(requestId: string): Promise<RequestMessageRow[]> {
    guard();
    // SECURITY DEFINER RPC (006g) bypasses request_messages SELECT policy chain
    const { data, error } = await supabase!.rpc("get_request_messages", {
      p_request_id: requestId,
      p_limit: 100,
    });
    if (error) throw error;
    // RPC returns newest-first; reverse for oldest-first display
    return ((data as RequestMessageRow[]) ?? []).reverse();
  },

  /** Post a message on a request */
  async postMessage(requestId: string, householdId: string, message: string): Promise<RequestMessageRow> {
    guard();
    // Use SECURITY DEFINER RPC (006f) to bypass RLS on insert
    const { data, error } = await supabase!.rpc("send_request_message", {
      p_request_id: requestId,
      p_household_id: householdId,
      p_message: message,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row as RequestMessageRow;
  },
};

// ────────────────────────────────────────────────────────────
// CIRCLE CHAT
// ────────────────────────────────────────────────────────────

export const circleChatApi = {
  async getMessages(circleId: string, limit = 50): Promise<CircleMessageRow[]> {
    guard();
    // SECURITY DEFINER RPC (006g) bypasses circle_messages SELECT policy chain
    const { data, error } = await supabase!.rpc("get_circle_messages", {
      p_circle_id: circleId,
      p_limit: limit,
    });
    if (error) throw error;
    // RPC returns newest-first; reverse to get oldest-first for display
    return ((data as CircleMessageRow[]) ?? []).reverse();
  },

  async postMessage(circleId: string, householdId: string, message: string): Promise<CircleMessageRow> {
    guard();
    // Use SECURITY DEFINER RPC (006f) to bypass RLS on insert
    const { data, error } = await supabase!.rpc("send_circle_message", {
      p_circle_id: circleId,
      p_household_id: householdId,
      p_message: message,
    });
    if (error) throw error;
    const row = Array.isArray(data) ? data[0] : data;
    return row as CircleMessageRow;
  },
};

// ────────────────────────────────────────────────────────────
// GROUP ORDERS
// ────────────────────────────────────────────────────────────

export interface GroupOrder {
  id: string;
  circle_id: string;
  shopper_household_id: string;
  shopper_household_name: string;
  store_name: string;
  pickup_day: string;
  deadline: string;
  status: 'open' | 'shopping' | 'delivered' | 'cancelled';
  notes?: string;
  created_at: string;
  item_count: number;
  my_item_count: number;
}

export interface GroupOrderItem {
  item_id: string;
  group_order_id: string;
  household_id: string;
  household_name: string;
  added_by: string;
  item_name: string;
  quantity: number;
  unit?: string;
  notes?: string;
  est_cost?: number;
  checked: boolean;
  created_at: string;
}

export const groupOrdersApi = {
  /** List all group orders for a circle */
  async list(circleId: string): Promise<GroupOrder[]> {
    guard();
    const { data, error } = await supabase!.rpc('list_group_orders', { p_circle_id: circleId });
    if (error) throw error;
    return (data as GroupOrder[]) ?? [];
  },

  /** Create a new group order (shopper initiates) */
  async create(params: {
    circleId: string;
    shopperHouseholdId: string;
    storeName: string;
    pickupDay: string;
    deadline: string;
    notes?: string;
  }): Promise<GroupOrder> {
    guard();
    const { data, error } = await supabase!.rpc('create_group_order', {
      p_circle_id: params.circleId,
      p_shopper_household_id: params.shopperHouseholdId,
      p_store_name: params.storeName,
      p_pickup_day: params.pickupDay,
      p_deadline: params.deadline,
      p_notes: params.notes ?? null,
    });
    if (error) throw error;
    return (Array.isArray(data) ? data[0] : data) as GroupOrder;
  },

  /** Get all items for an order (master list + per-household breakdown) */
  async getItems(orderId: string): Promise<GroupOrderItem[]> {
    guard();
    const { data, error } = await supabase!.rpc('get_group_order_detail', { p_order_id: orderId });
    if (error) throw error;
    return (data as GroupOrderItem[]) ?? [];
  },

  /** Submit / replace a household's items in an order */
  async submitItems(orderId: string, householdId: string, items: Array<{
    item_name: string; quantity: number; unit?: string; notes?: string; est_cost?: number;
  }>): Promise<GroupOrderItem[]> {
    guard();
    const { data, error } = await supabase!.rpc('add_group_order_items', {
      p_order_id: orderId,
      p_household_id: householdId,
      p_items: JSON.stringify(items),
    });
    if (error) throw error;
    return (data as GroupOrderItem[]) ?? [];
  },

  /** Shopper: mark an item as purchased */
  async checkItem(itemId: string, checked: boolean): Promise<void> {
    guard();
    const { error } = await supabase!.rpc('check_group_order_item', {
      p_item_id: itemId,
      p_checked: checked,
    });
    if (error) throw error;
  },

  /** Update order status (shopper only) */
  async updateStatus(orderId: string, status: GroupOrder['status']): Promise<void> {
    guard();
    const { error } = await supabase!.rpc('update_group_order_status', {
      p_order_id: orderId,
      p_status: status,
    });
    if (error) throw error;
  },
};
